import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ExternalLink, Award, Clock, Globe2, Briefcase, TrendingUp, Layers, GraduationCap, X, Share2, ChevronDown, Bell, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Empty } from "@/components/Shared";
import TrendingSkills from "@/components/TrendingSkills";

const shareCourse = (c, network) => {
  const text = `I found this AI course on WorldAIInfo.com — ${c.title} by ${c.provider} — ${c.is_free ? "Free" : (c.price || "Paid")} — check it out at worldaiinfo.com/courses`;
  const url = "https://worldaiinfo.com/courses";
  const links = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  };
  window.open(links[network], "_blank", "noopener,noreferrer");
};

const LEVELS = ["All", "Beginner", "Intermediate", "Advanced", "Executive"];
const CATEGORIES = ["All", "AI Fundamentals", "Machine Learning", "Deep Learning", "Generative AI", "Prompt Engineering", "NLP", "Computer Vision", "AI for Business", "AI Ethics", "MLOps", "AI Safety", "Data Science", "Python for AI"];
const DURATIONS = [["all", "All Durations"], ["5h", "Under 5 hours"], ["1m", "Under 1 month"], ["3m", "1-3 months"], ["6m", "3-6 months"]];
const CERTS = [["all", "All"], ["yes", "Certificate included"], ["no", "No certificate"]];
const LANGS = ["All", "English", "Arabic", "Hindi", "Spanish", "French", "Portuguese", "Chinese", "German"];
const SORTS = [["popular", "Most Popular"], ["rated", "Highest Rated"], ["free", "Free First"]];

const DEMAND_STYLE = {
  "Critical": "bg-[#EF4444]/15 text-[#f87171] border-[#EF4444]/30",
  "Very High": "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30",
  "High": "bg-[#00F0FF]/12 text-[#00F0FF] border-[#00F0FF]/30",
};
const demandKey = (d) => (d || "").startsWith("Critical") ? "Critical" : (d || "").startsWith("Very High") ? "Very High" : "High";
const durHours = (d) => { const m = (d || "").match(/(\d+)\s*hour/i); if (m) return +m[1]; const w = (d || "").match(/(\d+)\s*week/i); if (w) return +w[1] * 40; const mo = (d || "").match(/(\d+)\s*month/i); if (mo) return +mo[1] * 160; return 999; };

const selCls = "bg-[#0e1424] border border-white/10 rounded-md px-3 py-2 text-sm text-[#e2e8f0] focus:border-[#00F0FF] focus:outline-none";

function CourseCard({ c, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
      className={`card-surface rounded-xl p-5 flex flex-col ${c.is_free ? "ring-1 ring-[#22C55E]/25" : ""}`} data-testid={`course-card-${c.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        {c.is_free
          ? <span className="font-mono-tech text-[11px] uppercase font-bold px-2.5 py-1 rounded bg-[#22C55E] text-[#04121a]" data-testid={`course-free-${c.id}`}>Free</span>
          : <span className="font-mono-tech text-xs text-white">{c.price}</span>}
        <span className="font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border border-white/15 text-[#94A3B8]">{c.level}</span>
      </div>
      <p className="text-xs text-[#00F0FF] font-mono-tech mb-1">{c.provider}</p>
      <h3 className="font-display font-bold text-lg leading-snug">{c.title}</h3>
      <div className="flex items-center gap-3 text-xs text-[#94A3B8] font-mono-tech mt-2">
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration}</span>
        {c.certificate && <span className="flex items-center gap-1 text-[#4ade80]"><Award className="w-3.5 h-3.5" /> Certificate</span>}
      </div>
      <p className="text-sm text-[#94A3B8] mt-3 line-clamp-2 flex-1">{c.description}</p>
      {(c.career_outcomes || []).length > 0 && (
        <div className="mt-4">
          <p className="font-mono-tech text-[10px] uppercase text-[#64748B] mb-1.5">Jobs this unlocks</p>
          <div className="flex flex-wrap gap-1.5">
            {c.career_outcomes.slice(0, 3).map((j, idx) => (
              <span key={idx} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F59E0B]/12 text-[#fbbf24] border border-[#F59E0B]/25">{j}</span>
            ))}
          </div>
        </div>
      )}
      {c.live_jobs > 0 && c.jobs_query && (
        <Link to={`/jobs?search=${encodeURIComponent(c.jobs_query)}`} data-testid={`course-jobs-${c.id}`}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#00F0FF] hover:underline font-mono-tech">
          <Briefcase className="w-3.5 h-3.5" /> <span className="font-bold">{c.live_jobs}</span> live {c.jobs_query} jobs
        </Link>
      )}
      <div className="mt-1 text-[11px] text-[#64748B] font-mono-tech flex items-center gap-1"><Globe2 className="w-3 h-3" /> {c.countries || "Worldwide"} · {(c.languages || ["English"]).slice(0, 3).join(", ")}{(c.languages || []).length > 3 ? ` +${c.languages.length - 3}` : ""}</div>
      <div className="mt-4 flex items-center gap-2">
        <a href={c.url} target="_blank" rel="noreferrer" data-testid={`course-enroll-${c.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] text-[#04121a] font-semibold text-sm px-4 py-2.5 rounded-md hover:brightness-110 transition">
          Enroll Now <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <button onClick={() => shareCourse(c, "twitter")} data-testid={`course-share-twitter-${c.id}`} title="Share on Twitter/X"
          className="p-2.5 rounded-md border border-white/15 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition"><Share2 className="w-4 h-4" /></button>
        <button onClick={() => shareCourse(c, "linkedin")} data-testid={`course-share-linkedin-${c.id}`} title="Share on LinkedIn"
          className="px-2.5 py-2.5 rounded-md border border-white/15 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition text-xs font-mono-tech">in</button>
      </div>
    </motion.div>
  );
}

function SubmitCourseModal({ open, onClose }) {
  const empty = { title: "", provider: "", url: "", category: "AI Fundamentals", level: "Beginner", price: "", duration: "", certificate: false, languages: "", career_outcomes: "", contact_email: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/courses/submit", form);
      toast.success(data.message || "Submitted for review"); setForm(empty); onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const inp = "w-full bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0e1424] border border-white/10 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="submit-course-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl">Submit a Course</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#94A3B8]" /></button>
        </div>
        <p className="text-xs text-[#94A3B8] mb-4">Free for verified educational institutions. For featured placement, see <Link to="/advertise" className="text-[#00F0FF] hover:underline">advertising</Link>.</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Course title" value={form.title} onChange={(e) => set("title", e.target.value)} className={`${inp} md:col-span-2`} data-testid="sc-title" />
          <input required placeholder="Provider" value={form.provider} onChange={(e) => set("provider", e.target.value)} className={inp} data-testid="sc-provider" />
          <input required placeholder="Course URL" value={form.url} onChange={(e) => set("url", e.target.value)} className={inp} data-testid="sc-url" />
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="sc-category">{CATEGORIES.slice(1).map((c) => <option key={c}>{c}</option>)}</select>
          <select value={form.level} onChange={(e) => set("level", e.target.value)} className={inp} data-testid="sc-level">{LEVELS.slice(1).map((l) => <option key={l}>{l}</option>)}</select>
          <input placeholder="Price (e.g. Free / $49)" value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="sc-price" />
          <input placeholder="Duration" value={form.duration} onChange={(e) => set("duration", e.target.value)} className={inp} data-testid="sc-duration" />
          <input placeholder="Languages (comma separated)" value={form.languages} onChange={(e) => set("languages", e.target.value)} className={`${inp} md:col-span-2`} data-testid="sc-languages" />
          <input placeholder="Career outcomes (comma separated)" value={form.career_outcomes} onChange={(e) => set("career_outcomes", e.target.value)} className={`${inp} md:col-span-2`} data-testid="sc-outcomes" />
          <input required type="email" placeholder="Contact email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={`${inp} md:col-span-2`} data-testid="sc-email" />
          <label className="flex items-center gap-2 text-sm text-[#94A3B8] md:col-span-2"><input type="checkbox" checked={form.certificate} onChange={(e) => set("certificate", e.target.checked)} className="w-4 h-4 accent-[#22C55E]" data-testid="sc-cert" /> Certificate included</label>
          <button type="submit" disabled={busy} className="md:col-span-2 bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50" data-testid="sc-submit">{busy ? "Submitting…" : "Submit Course"}</button>
        </form>
      </div>
    </div>
  );
}

function CareerPathCard({ p }) {
  const [open, setOpen] = useState(false);
  const slug = p.title.replace(/\s+/g, "-").toLowerCase();
  return (
    <div className="card-surface rounded-xl p-5 flex flex-col" data-testid={`career-path-${slug}`}>
      <h3 className="font-display font-bold text-base leading-snug">{p.title}</h3>
      <span className={`self-start mt-2 font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${DEMAND_STYLE[demandKey(p.demand)]}`}>{p.demand}</span>
      <p className="text-xs text-[#64748B] font-mono-tech mt-3">Job-ready in {p.time_to_ready}</p>
      <div className="flex flex-wrap gap-1 mt-2">{(p.skills || []).slice(0, 4).map((s, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[#94A3B8] border border-white/10">{s}</span>)}</div>
      <div className="mt-3 text-xs text-[#94A3B8]"><span className="text-[#00F0FF] font-bold">{p.live_jobs}</span> live jobs</div>

      <Link to={`/jobs?search=${encodeURIComponent(p.title)}`} data-testid={`career-jobs-${slug}`}
        className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#00F0FF] hover:underline"><Briefcase className="w-3.5 h-3.5" /> View live jobs</Link>
    </div>
  );
}

function BundleCard({ b, i }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="rounded-2xl border border-[#00F0FF]/25 bg-[#0e1424] p-6 flex flex-col" data-testid={`bundle-${i}`}>
      <p className="font-mono-tech text-xs text-[#00F0FF] uppercase">{b.total} · {b.price}</p>
      <h3 className="font-display font-bold text-lg mt-2">{b.title}</h3>
      <ol className="mt-4 space-y-2 flex-1">
        {b.chapters.map((ch, ci) => {
          const isObj = typeof ch === "object";
          const isOpen = openIdx === ci;
          return (
            <li key={ci} className="rounded-lg border border-white/10 overflow-hidden">
              <button onClick={() => isObj && setOpenIdx(isOpen ? null : ci)} data-testid={`bundle-${i}-chapter-${ci}`}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-white/[0.03] transition">
                <span className="font-mono-tech text-[#00F0FF] text-sm">{ci + 1}.</span>
                <span className="text-sm text-[#e2e8f0] flex-1">{isObj ? ch.title : ch}</span>
                {isObj && <ChevronDown className={`w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
              </button>
              {isObj && (
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden" data-testid={`bundle-${i}-chapter-body-${ci}`}>
                      <div className="px-3 pb-3 pt-1">
                        <p className="text-xs text-[#00F0FF] font-mono-tech">{ch.provider} · {ch.duration}</p>
                        <p className="text-sm text-[#94A3B8] mt-1.5">{ch.description}</p>
                        <a href={ch.url} target="_blank" rel="noreferrer" data-testid={`bundle-${i}-enroll-${ci}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#00F0FF] hover:underline">Enroll in this course <ExternalLink className="w-3 h-3" /></a>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </li>
          );
        })}
      </ol>
      <p className="text-sm text-[#4ade80] font-mono-tech mt-4 pt-3 border-t border-white/10">{b.outcome}</p>
    </div>
  );
}

function ComingSoonCard({ c }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const notify = async () => {
    if (!email) return;
    try {
      await api.post("/courses/notify", { email, course: c.title });
      setDone(true);
      toast.success("You'll be notified when this course launches.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="card-surface rounded-xl p-5 flex flex-col" data-testid={`coming-soon-${c.title.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="self-start font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#8B5CF6]/15 text-[#a78bfa] border border-[#8B5CF6]/30">Coming soon</span>
      <h3 className="font-display font-bold text-base mt-2 leading-snug">{c.title}</h3>
      <p className="text-xs text-[#00F0FF] font-mono-tech mt-1">{c.provider}</p>
      <p className="text-xs text-[#64748B] mt-1 flex-1">{c.note}</p>
      {done ? (
        <div className="mt-3 text-sm text-[#4ade80] font-medium">✓ You're on the list!</div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
            data-testid="coming-soon-email"
            className="flex-1 bg-[#111827] border border-white/10 rounded-md px-2.5 py-1.5 text-xs focus:border-[#00F0FF] focus:outline-none" />
          <button onClick={notify} data-testid="coming-soon-notify"
            className="inline-flex items-center gap-1 bg-[#8B5CF6] text-white font-semibold px-3 py-1.5 rounded-md text-xs hover:brightness-110"><Bell className="w-3.5 h-3.5" /> Notify Me</button>
        </div>
      )}
    </div>
  );
}

export default function Courses() {
  const [all, setAll] = useState([]);
  const [stats, setStats] = useState({});
  const [paths, setPaths] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [priceFilter, setPriceFilter] = useState("all"); // all | free | paid
  const [level, setLevel] = useState("All");
  const [category, setCategory] = useState(() => {
    const c = new URLSearchParams(window.location.search).get("category");
    return c && CATEGORIES.includes(c) ? c : "All";
  });
  const [duration, setDuration] = useState("all");
  const [cert, setCert] = useState("all");
  const [lang, setLang] = useState("All");
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/courses"), api.get("/courses/stats"), api.get("/courses/career-paths"), api.get("/courses/bundles")])
      .then(([c, s, p, b]) => { setAll(c.data); setStats(s.data); setPaths(p.data); setBundles(b.data); })
      .finally(() => setLoading(false));
    api.get("/courses/coming-soon").then((r) => setComingSoon(r.data)).catch(() => {});
  }, []);

  const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const filtered = useMemo(() => {
    let list = all.filter((c) => {
      if (priceFilter === "free" && !c.is_free) return false;
      if (priceFilter === "paid" && c.is_free) return false;
      if (level !== "All" && !(c.level || "").toLowerCase().includes(level.toLowerCase())) return false;
      if (category !== "All" && c.category !== category) return false;
      if (lang !== "All" && !(c.languages || []).includes(lang)) return false;
      if (cert === "yes" && !c.certificate) return false;
      if (cert === "no" && c.certificate) return false;
      if (duration !== "all") {
        const h = durHours(c.duration);
        if (duration === "5h" && h >= 5) return false;
        if (duration === "1m" && h >= 160) return false;
        if (duration === "3m" && (h < 160 || h > 480)) return false;
        if (duration === "6m" && (h < 480 || h > 960)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = [c.title, c.provider, c.category, c.description, ...(c.career_outcomes || [])];
        if (!hay.some((v) => (v || "").toLowerCase().includes(q))) return false;
      }
      return true;
    });
    if (sort === "rated") list = [...list].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sort === "free") list = [...list].sort((a, b) => (b.is_free ? 1 : 0) - (a.is_free ? 1 : 0));
    else list = [...list].sort((a, b) => (b.enrollment || 0) - (a.enrollment || 0));
    return list;
  }, [all, priceFilter, level, category, duration, cert, lang, search, sort]);

  const filtersActive = priceFilter !== "all" || level !== "All" || category !== "All" || duration !== "all" || cert !== "all" || lang !== "All" || !!search;
  const resetFilters = () => { setPriceFilter("all"); setLevel("All"); setCategory("All"); setDuration("all"); setCert("all"); setLang("All"); setSearch(""); };
  const suggestedCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return CATEGORIES.slice(1).find((c) => c.toLowerCase().includes(q) || q.includes(c.toLowerCase())) || null;
  }, [search]);

  return (
    <div data-testid="courses-page">
      <Seo title="AI Courses 2026 — Free & Paid Learning Paths | WorldAIInfo" description="Explore top AI and machine-learning courses, certifications and career paths — with live job counts for every skill. Free-only filter available." canonicalPath="/courses" />
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-8">
        <p className="font-mono-tech text-xs uppercase tracking-widest text-[#00F0FF] mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Learn AI</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">AI Courses & Career Paths</h1>
            <p className="mt-4 text-[#94A3B8] max-w-2xl">Free and paid AI courses from the world's leading institutions — with the exact jobs each course unlocks.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-sm text-[#94A3B8]"><span className="w-2 h-2 rounded-full bg-[#10B981] live-dot" /> Curated &amp; regularly reviewed</span>
            <button onClick={() => setSubmitOpen(true)} data-testid="submit-course-btn" className="border border-white/15 text-sm px-4 py-2 rounded-md hover:border-[#00F0FF]/60 transition">Submit a Course</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8" data-testid="courses-stats">
          {[
            { key: "total", label: "Total Courses", value: stats.total, onClick: () => { setPriceFilter("all"); scrollToId("courses-grid-section"); } },
            { key: "free", label: "Free Courses", value: stats.free, onClick: () => { setPriceFilter("free"); scrollToId("courses-grid-section"); } },
            { key: "paid", label: "Paid Courses", value: stats.paid, onClick: () => { setPriceFilter("paid"); scrollToId("courses-grid-section"); } },
            { key: "career_paths", label: "Career Paths", value: stats.career_paths, onClick: () => scrollToId("career-paths-section") },
            { key: "languages", label: "Languages", value: stats.languages, onClick: () => scrollToId("courses-filter-bar") },
          ].map((s) => {
            const active = (s.key === "total" && priceFilter === "all") || (s.key === priceFilter);
            return (
              <button key={s.key} onClick={s.onClick} data-testid={`stat-${s.key}`}
                className={`card-surface rounded-xl p-4 text-left transition-all hover:border-[#00F0FF]/50 ${active ? "ring-2 ring-[#00F0FF] border-[#00F0FF]/60" : ""}`}>
                <div className="font-display font-extrabold text-3xl text-[#00F0FF]">{s.value ?? 0}</div>
                <div className="font-mono-tech text-xs text-[#94A3B8] mt-1">{s.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-30 bg-[#060911]/95 backdrop-blur border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 space-y-3">
          {/* Prominent FREE-only toggle — the biggest draw for our growing community */}
          <button onClick={() => setPriceFilter((v) => (v === "free" ? "all" : "free"))} data-testid="filter-free"
            className={`w-full flex items-center justify-center gap-2 text-base font-bold px-6 py-3.5 rounded-xl border-2 transition-all ${priceFilter === "free" ? "border-[#22C55E] text-[#04121a] bg-[#22C55E] shadow-[0_0_24px_rgba(34,197,94,0.4)]" : "border-[#22C55E] text-[#4ade80] bg-[#22C55E]/10 hover:bg-[#22C55E]/20"}`}>
            <GraduationCap className="w-5 h-5" />
            {priceFilter === "free" ? "✓ Showing FREE courses only — tap to show all" : "Show FREE courses only"}
          </button>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search courses or providers" data-testid="courses-search" className="w-full bg-[#0e1424] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={level} onChange={(e) => setLevel(e.target.value)} className={selCls} data-testid="filter-level">{LEVELS.map((l) => <option key={l} value={l}>{l === "All" ? "All Levels" : l}</option>)}</select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={selCls} data-testid="filter-category">{CATEGORIES.map((c) => <option key={c} value={c}>{c === "All" ? "All Categories" : c}</option>)}</select>
            <select value={duration} onChange={(e) => setDuration(e.target.value)} className={selCls} data-testid="filter-duration">{DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select value={cert} onChange={(e) => setCert(e.target.value)} className={selCls} data-testid="filter-cert">{CERTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select value={lang} onChange={(e) => setLang(e.target.value)} className={selCls} data-testid="filter-language">{LANGS.map((l) => <option key={l} value={l}>{l === "All" ? "All Languages" : l}</option>)}</select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={selCls} data-testid="filter-sort">{SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
        </div>
      </div>

      {/* Courses grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <TrendingSkills onSelect={(s) => setSearch(s)} />
        {loading ? <Empty text="Loading courses…" /> : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="courses-empty">
            <Search className="w-10 h-10 mx-auto text-[#334155] mb-4" />
            <p className="text-[#e2e8f0] font-display font-bold text-lg">No courses match your filters</p>
            <p className="text-sm text-[#94A3B8] mt-1">
              {search ? <>We couldn't find anything for "<span className="text-[#00F0FF]">{search}</span>".</> : "Try broadening your filters."}
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              {suggestedCategory && category !== suggestedCategory && (
                <button data-testid="courses-empty-suggest"
                  onClick={() => { setCategory(suggestedCategory); setSearch(""); }}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition">
                  <Sparkles className="w-3.5 h-3.5" /> Browse {suggestedCategory}
                </button>
              )}
              {filtersActive && (
                <button data-testid="courses-empty-clear" onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-[#00F0FF] text-[#04121a] font-semibold hover:brightness-110 transition">
                  <X className="w-3.5 h-3.5" /> Clear all filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
            {filtered.map((c, i) => <CourseCard key={c.id} c={c} i={i} />)}
          </div>
        )}
      </div>

      {/* Career Paths */}
      {paths.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6" data-testid="career-paths">
          <h2 className="font-display font-bold text-2xl mb-1 flex items-center gap-2"><TrendingUp className="w-6 h-6 text-[#FBBF24]" /> AI Career Paths</h2>
          <p className="text-sm text-[#94A3B8] mb-6">Live AI job counts from our board, updated continuously.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {paths.map((p) => <CareerPathCard key={p.title} p={p} />)}
          </div>
        </div>
      )}

      {/* Bundles */}
      {bundles.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6" data-testid="course-bundles">
          <h2 className="font-display font-bold text-2xl mb-6 flex items-center gap-2"><Layers className="w-6 h-6 text-[#00F0FF]" /> Learning Paths</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bundles.map((b, i) => <BundleCard key={i} b={b} i={i} />)}
          </div>
        </div>
      )}

      {/* Coming Soon teaser */}
      {comingSoon.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6" data-testid="coming-soon-section">
          <h2 className="font-display font-bold text-2xl mb-1 flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#a78bfa]" /> Coming Soon</h2>
          <p className="text-sm text-[#94A3B8] mb-6">New bootcamps and in-person programmes launching soon — be the first to know.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {comingSoon.map((c) => <ComingSoonCard key={c.title} c={c} />)}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <p className="text-xs text-[#64748B] leading-relaxed" data-testid="courses-disclaimer">
            Course information is verified at time of listing but pricing, availability and content may change. Always confirm details on the official
            provider website before enrolling.
            Some links may be affiliate links — WorldAIInfo.com may earn a small commission at no extra cost to you.
          </p>
        </div>
      </div>

      <SubmitCourseModal open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </div>
  );
}
